import { MaterialIcons } from "@expo/vector-icons";
import { Link } from "expo-router";
import { useState } from "react";
import { Platform, Pressable, StyleSheet, View } from "react-native";

import { useCustomTheme } from "../hooks/useCustomTheme";
import { Icon } from "./Icon";
import { NoticeBoxFrame } from "./NoticeBoxFrame";
import { ThemedText } from "./ThemedText";
import { ThemedTextWithBold } from "./ThemedTextWithBold";

type NoticeBoxProps = {
  title?: string;
  textContent: string;
  links?: {
    key: string;
    label: string;
    href: string;
  }[];
};

export const NoticeBox = ({ title, textContent, links }: NoticeBoxProps) => {
  const { colors, fonts } = useCustomTheme();
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const [focusedLink, setFocusedLink] = useState<string | null>(null);

  const textContainerPadding = title
    ? {
        paddingHorizontal: 32,
        paddingVertical: 16,
      }
    : {
        paddingHorizontal: 0,
        paddingVertical: 0,
      };

  return (
    <NoticeBoxFrame
      backgroundColor={colors.sectionBackground}
      borderColor={colors.lightMuted}
    >
      <View style={styles.content}>
        {title && (
          <View style={styles.titleTextContainer}>
            <View style={styles.iconContainer}>
              <MaterialIcons
                name="notification-important"
                size={24}
                color={colors.text}
              />
            </View>
            <ThemedText style={[fonts.heavy]}>{title}</ThemedText>
          </View>
        )}
        <View style={[styles.textContainer, textContainerPadding]}>
          <View>
            <ThemedTextWithBold textContent={textContent} />
          </View>
          {links && links.length > 0 && (
            <View style={styles.linkContainer}>
              {links.map((link) => (
                <Link key={link.key} href={link.href} asChild>
                  <Pressable
                    style={
                      Platform.OS === "web"
                        ? ({ outlineStyle: "none" } as any)
                        : undefined
                    }
                    onHoverIn={() => {
                      setHoveredLink(link.key);
                    }}
                    onHoverOut={() => {
                      setHoveredLink(null);
                    }}
                    onFocus={() => {
                      setFocusedLink(link.key);
                    }}
                    onBlur={() => {
                      setFocusedLink((current) =>
                        current === link.key ? null : current,
                      );
                    }}
                  >
                    <View
                      style={[
                        styles.link,
                        {
                          borderColor:
                            focusedLink === link.key
                              ? colors.palette.spinach30
                              : hoveredLink === link.key
                                ? colors.text
                                : "transparent",
                        },
                      ]}
                    >
                      <ThemedText style={[fonts.bold, styles.linkText]}>
                        {link.label}
                      </ThemedText>
                      <Icon iconSet="material-community" icon="chevron-right" />
                    </View>
                  </Pressable>
                </Link>
              ))}
            </View>
          )}
        </View>
      </View>
    </NoticeBoxFrame>
  );
};

const styles = StyleSheet.create({
  content: {
    flexShrink: 1,
  },
  iconContainer: {
    alignItems: "center",
    justifyContent: "center",
  },
  titleTextContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 8,
  },
  textContainer: {
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap: 20,
  },
  linkContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 16,
  },
  link: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 2,
    borderRadius: 6,
    borderColor: "transparent",
  },
  linkText: {
    lineHeight: 24,
    paddingHorizontal: 8,
  },
});
